import {
  useDirectBackend,
  useBusinessStore,
  type IDomainPaginatedList,
  useBugReporter,
} from "#imports";
import type { ItemSchedule } from "~/domain/item-schedule";
import { isUnderMinimumLeadTime } from "~/utils/scheduling";

/**
 * Composable for handling minimum lead time calculations for menu items.
 * Determines which items should be disabled based on their minimum lead time requirements
 * and the scheduled order time.
 *
 * @returns Object containing utility functions for minimum lead time operations
 */
export const useMinimumLeadTime = () => {
  const { menuRepo } = useDirectBackend();
  const { report } = useBugReporter();
  const business = useBusinessStore();

  /**
   * Fetches and calculates minimum lead time requirements for menu items.
   * Returns only items that are under their minimum lead time and should be disabled.
   *
   * @param outletId - The outlet identifier
   * @param plus - Array of product PLU codes to check
   * @param scheduledTime - Optional scheduled order time with day and time.
   *                        When undefined, the current time (now) is used.
   * @returns Record mapping PLU to minimum lead time in hours for items that should be disabled.
   *          Returns empty object if no items are under minimum lead time or if no PLUs provided.
   *
   * @example
   * ```ts
   * const { getMinimumLeadTimeConflicts } = useMinimumLeadTime();
   * const leadTimes = await getMinimumLeadTimeConflicts(
   *   'outlet-123',
   *   ['ITEM001', 'ITEM002'],
   *   { day: '2026-01-25', time: '18:00' }
   * );
   * // Returns: { 'ITEM001': 24 } - only items under minimum lead time
   * ```
   */
  const getMinimumLeadTimeConflicts = async (
    outletId: string,
    plus: string[],
    scheduledTime?: { day: string; time: string },
  ) => {
    if (plus.length === 0) return {};

    try {
      const response: IDomainPaginatedList<ItemSchedule> =
        await menuRepo.getItemSchedules({
          outletId,
          salesChannel: business.salesChannel,
          plus,
        });

      const itemSchedules = response.data;

      return itemSchedules.reduce(
        (acc, itemSchedule) => {
          const { minimumLeadTimeInMinutes } = itemSchedule.settings;

          const shouldDisable = isUnderMinimumLeadTime(
            minimumLeadTimeInMinutes,
            scheduledTime,
          );

          if (!shouldDisable) return acc;

          return {
            ...acc,
            [itemSchedule.plu]: minimumLeadTimeInMinutes,
          };
        },
        {} as Record<string, number>,
      );
    } catch (error) {
      report(error);
      return {};
    }
  };

  /**
   * Validates if items in a basket can be scheduled for a given time based on minimum lead time.
   *
   * @param outletId - The outlet identifier
   * @param items - Array of items with plu and name properties
   * @param scheduledTime - Optional scheduled order time with day and time
   * @param formatError - Function to format error message for a given item name
   * @returns Object with isValid boolean and errors string (null if valid)
   *
   * @example
   * ```ts
   * const { validateScheduledTimeForItems } = useMinimumLeadTime();
   * const result = await validateScheduledTimeForItems(
   *   'outlet-123',
   *   basket.items,
   *   { day: '2026-01-25', time: '18:00' },
   *   (itemName) => t('error.item_conflict', { itemName })
   * );
   * if (!result.isValid) {
   *   console.error(result.errors);
   * }
   * ```
   */
  const validateScheduleTime = async (
    outletId: string,
    items: Array<{ plu: string; name: string }>,
    scheduledTime: { day: string; time: string } | undefined,
    formatError: (itemName: string) => string,
  ): Promise<
    { isValid: false; errors: string } | { isValid: true; errors: null }
  > => {
    const itemsPlus = items.map((item) => item.plu);

    const itemsMinimumLeadTime = await getMinimumLeadTimeConflicts(
      outletId,
      itemsPlus,
      scheduledTime,
    );

    const hasMinimumLeadTimeIssues =
      Object.keys(itemsMinimumLeadTime).length > 0;

    if (hasMinimumLeadTimeIssues) {
      const errors = Object.entries(itemsMinimumLeadTime)
        .map(([plu]) => {
          const itemName = items.find((item) => item.plu === plu)?.name || "";
          return formatError(itemName);
        })
        .join("\n");

      return { isValid: false, errors };
    }

    return { isValid: true, errors: null };
  };

  return {
    getMinimumLeadTimeConflicts,
    validateScheduleTime,
  };
};
